import Dexie from "dexie";
import { isUndefined } from "lodash-es";
import { SortByKind } from "@/modules/lenses/types";
import type { AppStore } from "@/store/AppStore";
import {
  IndexedCollectionSyncUpdateValue,
  CollectionSearchParams,
  CollectionSearchReturnType,
  CollectionSearchReturnTypeMap,
  CollectionSearchSortByKind,
  CollectionIndexTuple,
  CollectionDataTransferObject,
} from "@/store/collections/types";
import { getAllWords } from "@/domains/search/get-all-words";

const DEFAULT_SORT_BY = SortByKind.LastModified;

export async function searchCollections<T extends CollectionSearchReturnType>(
  store: AppStore,
  params: CollectionSearchParams<T>
): Promise<CollectionSearchReturnTypeMap<T>> {
  const sortBy = params.sortBy ?? DEFAULT_SORT_BY;

  const filterIndex = isUndefined(params.filter?.isOwnedByMe)
    ? "is_available"
    : "is_available+is_owned_by_me";

  const filteredSortMap: Record<CollectionSearchSortByKind, string> = {
    [SortByKind.LastCreated]: `[${filterIndex}+created_at+model_id]`,
    [SortByKind.LastModified]: `[${filterIndex}+modified_at+model_id]`,
    [SortByKind.LastViewed]: `[${filterIndex}+last_viewed_at+model_id]`,
    [SortByKind.Alphabetical]: `[${filterIndex}+lowercase_title+model_id]`,
    [SortByKind.LastShared]: `[${filterIndex}+shared_with_me_at+model_id]`,
  };
  const betweenClause = getBetweenClause<T>(params);

  let query = store.collections.localTable
    .where(filteredSortMap[sortBy])
    .between(betweenClause[0], betweenClause[1])
    .filter(collection => {
      return (
        matchesQuery(collection, params.query) &&
        matchesShared(collection, params.filter?.isShared) &&
        matchesModifiedBy(collection, params.filter?.modifiedBy) &&
        matchesCreatedBy(collection, params.filter?.createdBy) &&
        matchesExcludeIds(collection, params.excludeIds) &&
        matchesFullTextQuery(collection, params.filter?.fullTextQuery) &&
        matchesIds(collection, params.filter?.ids) &&
        matchesImportBatchId(collection, params?.importBatchId)
      );
    });

  if (sortBy !== SortByKind.Alphabetical) query = query.reverse();

  if (params.limit) query = query.limit(params.limit);

  switch (params.returns) {
    case CollectionSearchReturnType.CollectionObservable: {
      const result = await query.primaryKeys();
      return store.collections.getMany(result) as unknown as CollectionSearchReturnTypeMap<T>;
    }
    case CollectionSearchReturnType.IndexedCollection:
      return query.toArray() as unknown as CollectionSearchReturnTypeMap<T>;
    case CollectionSearchReturnType.CollectionIndexTuple:
      return query.keys() as unknown as CollectionSearchReturnTypeMap<T>;
    case CollectionSearchReturnType.DataTransferObject:
      return mapTuples(
        (await query.keys()) as unknown as CollectionIndexTuple[]
      ) as unknown as CollectionSearchReturnTypeMap<T>;
    default:
      throw new Error(`Invalid return type: ${params.returns}`);
  }
}

export function getBetweenClause<T extends CollectionSearchReturnType>(
  params: CollectionSearchParams<T>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any[][] {
  if (params.filter?.isOwnedByMe === true) {
    return [
      [1, 1, Dexie.minKey, Dexie.minKey], // Filter out empty string values
      [1, 1, [[]], [[]]],
    ];
  }
  if (params.filter?.isOwnedByMe === false) {
    return [
      [1, 0, Dexie.minKey, Dexie.minKey], // Filter out empty string values
      [1, 0, [[]], [[]]],
    ];
  }
  // params.filter.isOwnedByMe is undefined
  return [
    [1, Dexie.minKey, Dexie.minKey, Dexie.minKey],
    [1, [[]], [[]], [[]]],
  ];
}

const matchesQuery = (collection: IndexedCollectionSyncUpdateValue, query?: string) => {
  return !query || collection.lowercase_title.includes(query.toLowerCase());
};

const matchesShared = (collection: IndexedCollectionSyncUpdateValue, isShared?: boolean) => {
  if (isShared === undefined) return true;
  return collection.is_shared === (isShared ? 1 : 0);
};

const matchesModifiedBy = (collection: IndexedCollectionSyncUpdateValue, modifiedBy?: string[]) => {
  if (!modifiedBy?.length) return true;
  const modifiedBySet = new Set(collection.model_data.modified_by_space_account_ids);
  return modifiedBy.some(id => modifiedBySet.has(id));
};

const getCreatorSpaceAccountId = (collection: IndexedCollectionSyncUpdateValue): string => {
  return (
    collection.model_data.attributed_created_by_space_account_id ||
    collection.model_data.created_by_space_account_id ||
    collection.model_data.owned_by_space_account_id
  );
};

const matchesCreatedBy = (
  collection: IndexedCollectionSyncUpdateValue,
  createdBy: string[] | undefined
) => {
  if (!createdBy?.length) return true;
  return createdBy.includes(getCreatorSpaceAccountId(collection));
};

const matchesExcludeIds = (collection: IndexedCollectionSyncUpdateValue, excludeIds?: string[]) => {
  if (!excludeIds?.length) {
    return true;
  }

  return !excludeIds.includes(collection.model_id);
};

const matchesFullTextQuery = (
  collection: IndexedCollectionSyncUpdateValue,
  fullTextQuery?: string
) => {
  if (!fullTextQuery) return true;

  const terms = getAllWords(fullTextQuery);
  const content = getAllWords(`${collection.lowercase_title} ${collection.model_data.description}`);

  return terms.every(term => content.some(word => term.startsWith(word)));
};

const matchesIds = (collection: IndexedCollectionSyncUpdateValue, ids?: string[]) => {
  if (!ids?.length) return true;
  return ids.includes(collection.model_id);
};

const matchesImportBatchId = (
  collection: IndexedCollectionSyncUpdateValue,
  importBatchId?: string
) => {
  if (!importBatchId) return true;
  return collection.import_batch_id === importBatchId;
};

// This is a helper function to transform convoluted Dexie tuples into named objects
// It suffers so you don't have to!
// Please update if more / fewer indexes are being retrieved
const mapTuples = (tuples: CollectionIndexTuple[]): CollectionDataTransferObject[] => {
  return tuples.map(tuple => {
    if (tuple.length === 4) {
      const [isAvailable, isOwnedByMe, datetime, modelId] = tuple;
      return {
        isAvailable,
        isOwnedByMe,
        datetime,
        modelId,
      };
    }

    const [isAvailable, datetime, modelId] = tuple;
    return {
      isAvailable,
      datetime,
      modelId,
    };
  });
};
